import axios from "axios";
import { PaymentIntentData } from "../models/paymentIntent";
import { PaymentStatusData } from "../models/paymentStatus";
import { SERVER_URL } from "../config";
import { Authorization } from "./authorization";
import { SubscriptionData } from "../models/subscription";

export const createPaymentIntent = async (voucherCode: string | undefined, videoId: String, addAnalysis: boolean, affiliatePrePaidToken: string | null): Promise<PaymentIntentData | null> => {
  const res = await axios.post<PaymentIntentData>(SERVER_URL + "/payment/paymentIntent", { voucherCode, videoId, addAnalysis, affiliateToken: affiliatePrePaidToken }, {
    headers: {
      Authorization: await Authorization.AuthToken()
    }
  });
  if (res) {
    return res.data;
  }
  return null;
}

export const syncSubscription = async () => {
  await axios.post(SERVER_URL + "/payment/syncSubscription", {}, {
    headers: {
      Authorization: await Authorization.AuthToken()
    }
  });
}

export const paymentSubscription = async (voucherCode: string | undefined, videoId: string) => {
  return await axios.post<SubscriptionData>(SERVER_URL + "/payment/subscription", { voucherCode, videoId }, {
    headers: {
      Authorization: await Authorization.AuthToken()
    }
  });
}

export const getPaymentStatus = async (videoId: string): Promise<PaymentStatusData> => {
  const res = await axios.get<PaymentStatusData>(SERVER_URL + "/payment/status", {
    params: { videoId },
    headers: {
      Authorization: await Authorization.AuthToken()
    }
  });
  return res.data;
}

export type PaymentConfirmation = 'confirmed' | 'pending';

export interface PaymentConfirmationOptions {
  timeoutMs?: number;
  intervalMs?: number;
  // Injectable so tests can run without real time passing.
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Poll the recording's payment status until the backend has it as paid.
 *
 * Card payments are marked paid by the Stripe webhook, which normally lands within seconds but is
 * outside our control, so this gives up after `timeoutMs` and reports 'pending' rather than a
 * failure: by this point Stripe has already confirmed the charge, so the customer must not be
 * asked to pay again. An error from the status endpoint counts as "not confirmed yet".
 */
export const waitForPaymentConfirmation = async (videoId: string, options: PaymentConfirmationOptions = {}): Promise<PaymentConfirmation> => {
  const { timeoutMs = 60000, intervalMs = 2000, now = Date.now, sleep = defaultSleep } = options;
  const deadline = now() + timeoutMs;

  for (;;) {
    try {
      const status = await getPaymentStatus(videoId);
      if (status.isPaid) {
        return 'confirmed';
      }
    } catch (e) {
      console.error(e);
    }

    const remaining = deadline - now();
    if (remaining <= 0) {
      return 'pending';
    }
    await sleep(Math.min(intervalMs, remaining));
  }
}
